import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "ring", "startLabel", "duration", "sessionLabel", "toggleButton", "playIcon", "pauseIcon"]
  static values = {
    storageKey: String,
    defaultMinutes: { type: Number, default: 25 }
  }

  connect() {
    this.restore()
    this.render()
    if (this.running) this.startTicker()
  }

  disconnect() {
    this.stopTicker()
    this.persist()
  }

  toggle() {
    this.running ? this.pause() : this.start()
  }

  start() {
    if (this.remainingSeconds <= 0) this.remainingSeconds = this.durationSeconds

    this.running = true
    this.endsAt = Date.now() + (this.remainingSeconds * 1000)
    this.element.dataset.running = "true"
    this.startTicker()
    this.persist()
    this.render()
  }

  pause() {
    this.syncRemaining()
    this.running = false
    this.endsAt = null
    this.element.dataset.running = "false"
    this.stopTicker()
    this.persist()
    this.render()
  }

  reset() {
    this.running = false
    this.endsAt = null
    this.remainingSeconds = this.durationSeconds
    this.element.dataset.running = "false"
    this.element.dataset.complete = "false"
    this.stopTicker()
    this.persist()
    this.render()
  }

  changeDuration() {
    const minutes = Number(this.durationTarget.value)
    if (!Number.isFinite(minutes) || minutes < 1 || minutes > 120) return

    this.durationSeconds = minutes * 60
    this.reset()
  }

  startTicker() {
    this.stopTicker()
    this.ticker = window.setInterval(() => {
      this.syncRemaining()
      this.render()

      if (this.remainingSeconds <= 0) this.complete()
    }, 250)
  }

  stopTicker() {
    if (this.ticker) window.clearInterval(this.ticker)
    this.ticker = null
  }

  syncRemaining() {
    if (!this.running || !this.endsAt) return
    this.remainingSeconds = Math.max(0, Math.ceil((this.endsAt - Date.now()) / 1000))
  }

  complete() {
    this.running = false
    this.endsAt = null
    this.remainingSeconds = 0
    this.element.dataset.running = "false"
    this.element.dataset.complete = "true"
    this.stopTicker()
    this.persist()
    this.render()
  }

  restore() {
    this.durationSeconds = this.defaultMinutesValue * 60
    this.remainingSeconds = this.durationSeconds
    this.running = false
    this.endsAt = null

    if (!this.hasStorageKeyValue) return

    try {
      const state = JSON.parse(window.localStorage.getItem(this.storageKeyValue))
      if (!state) return

      const duration = Number(state.durationSeconds)
      if (Number.isFinite(duration) && duration >= 60 && duration <= 7200) {
        this.durationSeconds = duration
      }

      this.running = state.running === true
      this.endsAt = this.running ? Number(state.endsAt) : null
      this.remainingSeconds = this.running
        ? Math.max(0, Math.ceil((this.endsAt - Date.now()) / 1000))
        : Math.max(0, Number(state.remainingSeconds) || this.durationSeconds)

      if (this.remainingSeconds <= 0) {
        this.running = false
        this.endsAt = null
      }
    } catch (_error) {
      // The timer remains usable when local storage is unavailable or invalid.
    }
  }

  persist() {
    if (!this.hasStorageKeyValue) return

    try {
      window.localStorage.setItem(this.storageKeyValue, JSON.stringify({
        durationSeconds: this.durationSeconds,
        remainingSeconds: this.remainingSeconds,
        running: this.running,
        endsAt: this.endsAt
      }))
    } catch (_error) {
      // No server write is required for a focus session.
    }
  }

  render() {
    const minutes = Math.floor(this.remainingSeconds / 60)
    const seconds = this.remainingSeconds % 60
    this.displayTarget.textContent = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    this.startLabelTarget.textContent = this.running ? "Pause" : (this.remainingSeconds === this.durationSeconds ? "Start focus" : "Resume")
    this.playIconTarget.classList.toggle("hidden", this.running)
    this.pauseIconTarget.classList.toggle("hidden", !this.running)
    this.toggleButtonTarget.setAttribute("aria-label", this.running ? "Pause focus timer" : "Start focus timer")
    this.toggleButtonTarget.setAttribute("title", this.running ? "Pause" : "Start focus")
    this.element.dataset.running = String(this.running)
    this.sessionLabelTarget.textContent = this.remainingSeconds === 0 ? "Focus session complete" : "Focus session"
    this.durationTarget.value = String(this.durationSeconds / 60)

    const elapsedRatio = this.durationSeconds > 0
      ? (this.durationSeconds - this.remainingSeconds) / this.durationSeconds
      : 0
    this.ringTarget.style.setProperty("--focus-progress", `${Math.max(0, Math.min(1, elapsedRatio)) * 360}deg`)
  }
}
